import { getCookieSnapshot } from "./cookies";
import { isOfficialApkUserAgent, parseUAFlags } from "./ua";
import type { ClientEnvironment } from "./types";

type DetectInput = {
  userAgent?: string;
  cookieString?: string;
};

const getDefaultUA = () =>
  typeof navigator !== "undefined" ? navigator.userAgent || "" : "";

const getDefaultCookies = () =>
  typeof document !== "undefined" ? document.cookie || "" : "";

export const detectClientEnvironment = (input: DetectInput = {}): ClientEnvironment => {
  const cookieString = input.cookieString ?? getDefaultCookies();
  const cookies = getCookieSnapshot(cookieString);

  const uaRaw = input.userAgent ?? getDefaultUA();
  const ua = parseUAFlags(uaRaw);

  const reasons: string[] = [];

  // Priority rules:
  // 1) is_opened_from_apk=true => official native WebView
  // 2) 9BETIN/<version> UA => official native WebView first-load fallback
  // 3) gpsAdid cookie exists AND no official APK signal => Flutter WebView
  // 4) Facebook family in-app browser via UA
  // 5) Remaining => browser (pure browser fallback)

  if (cookies.isOpenedFromApk) {
    reasons.push("cookie:is_opened_from_apk=true");
    return {
      bucket: "native_webview",
      kind: "native_webview",
      confidence: "high",
      reasons,
      ua,
      cookies,
      isNativeWebView: true,
      isFlutterWebView: false,
      isFacebookFamilyIAB: false,
      isPureBrowser: false,
    };
  }

  if (isOfficialApkUserAgent(uaRaw)) {
    reasons.push("ua:9BETIN/<version>");
    return {
      bucket: "native_webview",
      kind: "native_webview",
      confidence: "high",
      reasons,
      ua,
      cookies,
      isNativeWebView: true,
      isFlutterWebView: false,
      isFacebookFamilyIAB: false,
      isPureBrowser: false,
    };
  }

  if (cookies.gpsAdid) {
    reasons.push("cookie:gpsAdid|gps_adid present");
    return {
      bucket: "flutter_webview",
      kind: "flutter_webview",
      confidence: "high",
      reasons,
      ua,
      cookies,
      isNativeWebView: false,
      isFlutterWebView: true,
      isFacebookFamilyIAB: false,
      isPureBrowser: false,
    };
  }

  if (ua.isInstagram || ua.isMessenger || ua.isFacebook || ua.isFacebookIAB) {
    if (ua.isInstagram) reasons.push("ua:Instagram");
    if (ua.isMessenger) reasons.push("ua:Messenger");
    if (ua.isFacebook || ua.isFacebookIAB) reasons.push("ua:Facebook");
    return {
      bucket: "facebook_related",
      kind: "facebook_related",
      confidence: "medium",
      reasons,
      ua,
      cookies,
      isNativeWebView: false,
      isFlutterWebView: false,
      isFacebookFamilyIAB: true,
      isPureBrowser: false,
    };
  }

  reasons.push("default:browser");
  return {
    bucket: "browser",
    kind: "browser",
    confidence: "medium",
    reasons,
    ua,
    cookies,
    isNativeWebView: false,
    isFlutterWebView: false,
    isFacebookFamilyIAB: false,
    isPureBrowser: true,
  };
};

export default detectClientEnvironment;
